import React, { useContext, useEffect, useState } from 'react';
import CartContext from '../../../../contexts/CartContext';
import TotalsPanel from '../../cart/TotalsPanel';
import ContactPanel from '../ContactPanel';
import DiscountOptions from '../payment/DiscountOptions';
import PaymentDescription from '../payment/PaymentDescription';
import PaymentOptions from '../payment/PaymentOptions';
import PointsRedemptionPanel from '../PointsRedemptionPanel';
import ShippingPanel from '../ShippingPanel';
import ConfigContext from '../../../../contexts/ConfigContext';
import API, { uuidv4 } from '../../../../API';
import { useCustomer } from '../../../../contexts/CustomerContext';
import MuiAlert, { AlertProps } from '@mui/material/Alert';
import { useHistory } from "react-router-dom";
import IfTrue from '../../../conditions/IfTrue';

type Props = {
  setNextName: React.Dispatch<React.SetStateAction<string>>;
  setPreNextAction: React.Dispatch<React.SetStateAction<() => () => Promise<boolean>>>;
  setActiveStep?: (step: number) => void;
}

const Step4 = ({setNextName, setPreNextAction, setActiveStep}: Props) => {
  const history = useHistory();
  const {config} = useContext(ConfigContext);
  const {cart, setCartState} = useContext(CartContext);
  const { idToken, refreshPoints } = useCustomer();
  const [getPaymentToken, setGetPaymentToken] = React.useState<undefined | (() => () => Promise<any>)>(undefined);
  
  const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(
    props,
    ref,
  ) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);


  const setErrorMessage = (msg: string | undefined) => {
    setCartState({
      ...cart,
      lastError: msg
    })
  }

  const doSubmitOrder = async (): Promise<boolean> => {
    if (cart.items && getPaymentToken) {

      // 1. Submit the order
      let orderId: string | undefined;
      // The token is a special ID that is only visible during creation, and thereafter by the admin. This
      // means it can briefly be used as a secure way to do anonymous state changes, cause this data is
      // only briefly available in this scope.
      let token: string = '';
      try {
        // backroom-server fetches the authoritative shipping cost + item
        // prices from Node; we just send the cart as-is. When signed in,
        // attach the Firebase token so the order ties to the customer.
        const firebaseToken = await idToken();
        const result: any = await API.submitOrder(cart, firebaseToken);
        orderId = result.id;
        if (result.token) {
          token = result.token;
        }
      }
      catch (e: any) {
        console.error(JSON.stringify(e));
        const message = e.message ? e.message : "Unknown error submitting order";
        setErrorMessage(message);
        return false;
      }

      if (!orderId) {
        setErrorMessage("Error submitting order");
        return false;
      }

      let getPaymentTokenCallback = undefined;

      switch (cart.paymentMethod) {
        case "emt":
          // No payment
          break;
        
        default:
          // Pay for the order
          try {
            const getPaymentTokenMethod = getPaymentToken as any as () => Promise<any>;
            // Recorded BEFORE the payment window opens, because the
            // worst case leaves no other trace: if the SDK never calls
            // back at all, the customer sits on a spinner and closes the
            // tab, and nothing else here ever runs. An 'opened' with no
            // ending, on an order that was never paid, is that case.
            if (token) {
              API.reportCheckoutEvent(orderId, token, 'opened');
            }
            const paymentData = await getPaymentTokenMethod();

            getPaymentTokenCallback = paymentData.callback;

            setErrorMessage(undefined);

            await API.createPayment(
              paymentData.provider,
              paymentData.location,
              paymentData.environment,
              paymentData.token,
              orderId);

            setErrorMessage(undefined);
          }
          catch (e: any) {
            console.error('Payment failed', e);

            // What actually happened, in the SDK's own vocabulary:
            // 'BeforePayment' (closed before entering a card),
            // 'DuringPayment' (closed mid-payment — the 3DS window),
            // 'sdk_error', 'declined'. Without this the order is just
            // 'pending' and every cause looks the same afterwards.
            if (token) {
              API.reportCheckoutEvent(
                orderId,
                token,
                e?.stage ?? 'payment_failed',
                e?.reason ?? e?.message ?? String(e),
              );
            }
            // Show the user what happened. Most common reason here is the
            // payment processor rejecting the card.
            const message = e?.message ? e.message : 'Payment failed. Please check your card details and try again.';
            setErrorMessage(message);

            // Best-effort cancel of the just-created order. Skip if we
            // don't have a token (we can't auth the cancel without it) and
            // swallow errors so we don't double-fault the user with an
            // unhandled rejection.
            if (token) {
              try {
                await API.cancelOrderById(orderId, token);
              } catch (cancelErr) {
                console.warn('Failed to cancel order after payment error', cancelErr);
              }
            }

            if (getPaymentTokenCallback) {
              await getPaymentTokenCallback(false);
            }
            // Deliberately KEEP cardholderAddress on a failed attempt.
            // Clearing it here broke retry two ways: the TextFields in
            // PaysafePaymentProvider flipped controlled -> uncontrolled
            // (React leaves the typed text in the DOM but MUI un-shrinks
            // the label, so the label sits on top of the value), and
            // NextButton's STEP4 card branch requires firstName +
            // lastName + postal + country, so the Pay button went dead.
            // Country was unrecoverable: CountrySelect keeps its own
            // state and only writes to the cart on mount or user change,
            // so it still displayed "Canada" while the cart had none.
            return false;
          }

          break;
      }

      // If the order is submitted and paid, clear the cart
      const newCart = {
        ...cart,
        items: {},
        notes: undefined,
        lastOrderId: orderId,
        discountAmount: 0,
        discountCode: undefined,
        // One-shot field — clear it so a subsequent order doesn't accidentally
        // resubmit it (and so the input is empty when first_purchase flips).
        friendCode: undefined,
        // Same story for pointsToUse — applied to this order only.
        pointsToUse: undefined,
        paymentMethod: undefined,
        deliveryDate: undefined,
        live: undefined,
        // Cardholder details are per-attempt — clearing means the next
        // order's payment step pre-fills fresh from customerAddress.
        cardholderAddress: {},
      };
      delete newCart.checkoutStep;
      setCartState(newCart);

      if (setActiveStep) {
        setActiveStep(0);
      }

      if (getPaymentTokenCallback) {
        await getPaymentTokenCallback(true);
      }

      // Refresh points + friend-code state in the background so the
      // /account page reflects the new credit and `first_purchase` flips
      // to false. Best-effort: failures here don't block the redirect.
      refreshPoints().catch((err) =>
        console.warn('[step4] refreshPoints after order failed:', err),
      );

      // Everything succeeded!
      history.push(`/order/${orderId}`);
      return true;
    }

    return false;
  }

  useEffect(() => {
    setPreNextAction(() => async () => {return await doSubmitOrder()});
  }, [getPaymentToken]);


  return (
    <>
      <div className='checkout-parent'>
        <div className='checkout-main'>
          <IfTrue condition={!!cart.lastError}>
            <Alert severity="error">
            {cart.lastError}
            </Alert>
          </IfTrue>
          {/* <DiscountOptions/> */}
          <PointsRedemptionPanel/>
          <PaymentOptions setNextName={setNextName}/>
          <PaymentDescription getPaymentToken={getPaymentToken} setGetPaymentToken={setGetPaymentToken}/>
        </div>
        <div className='checkout-right'>
          <TotalsPanel hideDiscount={false}/>
          <ContactPanel />
          <ShippingPanel />
        </div>
      </div>
    </>
  )
}

export default Step4
